import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

interface PmContractProjectState {
  id: string;
  accountManagerId: string;
  accountSaleId: string;
  projectAuthorId: string;
  chiefTechnicianId: string;
  companyId: string;
  pmOfferId: any;
  number: string;
  date: string;
  dateStart: string;
  dateEnd: string;
  enumPmOfferContractType: any;
  enumPmOfferContractTypeId: any;
  enumPmContractProjectStatus: any;
  enumPmContractProjectStatusId: any;
  metadata: any;
  remarks: any;
  haveFile: any;
  file: any;
  registrationNumber: any;
  accountManager: any;
  projectAuthor: any;
  accountSale: any;
  chiefTechnician: any;
  company: any;
  costPerKilometer: any;
  pmContractIntervals: any[];
  pmContractWorkPlaceItems: any[];
  pmContractLaborExtras: any[];
  pmContractCommentsTab: any[];
  location: any[];
  technician: any[];
  interventionFileProjects: any[];
  pmContractWorkPlaceTransportCosts: any[];
  pmContractSectionFiles: any[];
  equipmentTab: {
    [key: string]: any;
  };
  laborTab: {
    [key: string]: any;
  };
  props: {
    [key: string]: any;
  };
}

const currentDate = moment().format('YYYY-MM-DD');
const currentDateEnd = moment().add(1, 'year').format('YYYY-MM-DD');

const initialState: PmContractProjectState = {
  id: '',
  accountManagerId: '',
  accountSaleId: '',
  projectAuthorId: '',
  chiefTechnicianId: '',
  companyId: '',
  pmOfferId: '',
  enumPmOfferContractTypeId: 0,
  enumPmContractProjectStatusId: 0,
  number: '',
  date: currentDate,
  dateStart: currentDate,
  dateEnd: currentDateEnd,
  remarks: '',
  registrationNumber: '',
  haveFile: false,
  file: '',
  metadata: null,
  accountManager: null,
  projectAuthor: null,
  accountSale: null,
  chiefTechnician: null,
  company: null,
  enumPmOfferContractType: null,
  enumPmContractProjectStatus: null,
  costPerKilometer: 2.5,
  pmContractIntervals: [],
  pmContractWorkPlaceItems: [],
  pmContractLaborExtras: [],
  pmContractCommentsTab: [],
  location: [],
  technician: [],
  interventionFileProjects: [],
  pmContractWorkPlaceTransportCosts: [],
  pmContractSectionFiles: [],
  equipmentTab: { workPlaceId: null },
  laborTab: { laborPriceOnHour: 300 },
  props: { searchTerm: '', skip: 1, take: 10, totalItems: 0 },
};

const pmContractProjectSlice = createSlice({
  name: 'pmContractProject',
  initialState,
  reducers: {
    setPmContractProject: (state, action: PayloadAction<Partial<PmContractProjectState>>) => {
      return { ...state, ...action.payload };
    },
    updateField: (state: any, action: PayloadAction<{ field: any; value: any }>) => {
      const { field, value } = action.payload;
      state[field] = value;
    },
    updateEquipmentTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.equipmentTab = {
        ...state.equipmentTab,
        [field]: value,
      };
    },
    updateLaborTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.laborTab = {
        ...state.laborTab,
        [field]: value,
      };
    },
    addPmContractProjectWorkPlaceItem: (state, action) => {
      state.pmContractWorkPlaceItems.push(action.payload);
    },
    updatePmContractProjectWorkPlaceItem: (state, action: PayloadAction<{ index: number; updates: any }>) => {
      const { index, updates } = action.payload;
      if (state.pmContractWorkPlaceItems[index]) {
        state.pmContractWorkPlaceItems[index] = {
          ...state.pmContractWorkPlaceItems[index],
          ...updates,
        };
      }
    },
    removePmContractProjectWorkPlaceItem: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmContractWorkPlaceItems.length) {
        state.pmContractWorkPlaceItems.splice(index, 1);
      }
    },
    // **Add interval actions**
    addPmContractProjectInterval: (state, action: PayloadAction<any>) => {
      state.pmContractIntervals.push(action.payload);
    },
    updatePmContractProjectInterval: (state, action: PayloadAction<{ index: number; updates: Partial<any> }>) => {
      const { index, updates } = action.payload;
      if (state.pmContractIntervals[index]) {
        state.pmContractIntervals[index] = {
          ...state.pmContractIntervals[index],
          ...updates,
        };
      }
    },
    removePmContractProjectInterval: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmContractIntervals.length) {
        state.pmContractIntervals.splice(index, 1);
      }
    },
    //
    addPmContractProjectLaborExtra: (state, action) => {
      state.pmContractLaborExtras.push(action.payload);
    },
    addPmContractProjectComment: (state, action: PayloadAction<any>) => {
      state.pmContractCommentsTab.push(action.payload);
    },
    updatePmContractProjectComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        updates: Partial<any>;
      }>
    ) => {
      const { departmentName, updates } = action.payload;
      const index = state.pmContractCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (index !== -1) {
        state.pmContractCommentsTab[index] = {
          ...state.pmContractCommentsTab[index],
          ...updates,
        };
      } else {
        state.pmContractCommentsTab.push({
          departmentName,
          ...updates,
        });
      }
    },
    // Remove a file from a comment
    removePmContractProjectComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        fileUid: string;
      }>
    ) => {
      const { departmentName, fileUid } = action.payload;
      const commentIndex = state.pmContractCommentsTab.findIndex(
        (comment) => comment.departmentName === departmentName
      );

      if (commentIndex !== -1) {
        const comment = state.pmContractCommentsTab[commentIndex];

        const updatedFiles = (comment.pmContractCommentFiles || []).filter(
          (file: any) => file.uid !== fileUid && file.id !== fileUid
        );

        state.pmContractCommentsTab[commentIndex] = {
          ...comment,
          pmContractCommentFiles: updatedFiles,
        };
      }
    },

    addPmContractWorkPlaceTransportCost: (
      state: any,
      action: PayloadAction<{
        pmContractId: string;
        workPlaceId: string;
        kilometers: number;
        time: number;
        cost: number;
      }>
    ) => {
      state.pmContractWorkPlaceTransportCosts.push(action.payload);
    },

    // ✅ Update transport cost entry
    updatePmContractWorkPlaceTransportCost: (
      state: any,
      action: PayloadAction<{
        pmContractId: string;
        workPlaceId: string;
        kilometers: number;
        time: number;
        cost: number;
      }>
    ) => {
      const { pmContractId, workPlaceId, kilometers, time, cost } = action.payload;
      const index = state.pmContractWorkPlaceTransportCosts.findIndex((item: any) => item.workPlaceId === workPlaceId);

      if (index !== -1) {
        // Update existing entry
        state.pmContractWorkPlaceTransportCosts[index] = {
          ...state.pmContractWorkPlaceTransportCosts[index],
          pmContractId,
          kilometers,
          time,
          cost,
        };
      } else {
        // Add new entry if it doesn't exist
        state.pmContractWorkPlaceTransportCosts.push({
          pmContractId,
          workPlaceId,
          kilometers,
          time,
          cost,
        });
      }
    },

    uploadFile: (state: any, action: PayloadAction<{ key: string; file: any }>) => {
      state[action.payload.key].push(action.payload.file);
    },
    deleteFile: (state: any, action: PayloadAction<{ key: string; uid: string }>) => {
      state[action.payload.key] = state[action.payload.key].filter(
        (file: any) => file.uid !== action.payload.uid && file.id !== action.payload.uid
      );
    },
    updateProps: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      state.props = {
        ...state.props,
        [field]: value,
      };
    },
    // Add a new section file
    updatePmContractSectionFile: (state, action: PayloadAction<any>) => {
      const newFile = action.payload;

      // Generate a unique ID for the file if it doesn't have one
      if (!newFile.id) {
        newFile.id = '';
      }

      // Check if file already exists (by uid)
      const existingFileIndex = state.pmContractSectionFiles.findIndex((file: any) => file.uid === newFile.uid);

      if (existingFileIndex !== -1) {
        // Update existing file
        state.pmContractSectionFiles[existingFileIndex] = {
          ...state.pmContractSectionFiles[existingFileIndex],
          ...newFile,
        };
      } else {
        // Add new file
        state.pmContractSectionFiles.push(newFile);
      }
    },
    removePmContractSectionFile: (state, action: PayloadAction<string>) => {
      const fileUid = action.payload;
      state.pmContractSectionFiles = state.pmContractSectionFiles.filter(
        (file: any) => file.uid !== fileUid && file.id !== fileUid
      );
    },
  },
});

export const {
  setPmContractProject,
  updateField,
  updateEquipmentTab, // Export the new action
  updateLaborTab,
  addPmContractProjectWorkPlaceItem,
  updatePmContractProjectWorkPlaceItem,
  removePmContractProjectWorkPlaceItem,
  addPmContractProjectInterval,
  updatePmContractProjectInterval,
  removePmContractProjectInterval,
  addPmContractProjectLaborExtra,
  addPmContractProjectComment,
  updatePmContractProjectComment,
  removePmContractProjectComment,
  addPmContractWorkPlaceTransportCost,
  updatePmContractWorkPlaceTransportCost,
  uploadFile,
  deleteFile,
  updateProps,
  updatePmContractSectionFile,
  removePmContractSectionFile,
} = pmContractProjectSlice.actions;

export default pmContractProjectSlice.reducer;
